/**
 * Scholarship application — the learner's side of the approval flow.
 *
 * A family tells us their situation, we look at it, and if we approve it their
 * scholarship code unlocks at checkout. Nothing here promises an outcome and
 * nothing is scored: it is a request, and a person reads it.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

/** The award we ask about, and the code that carries it. */
const AWARDS = [
  { code: "FULLSCHOLAR100", label: "Full scholarship — family income below ₹1 lakh" },
  { code: "IMPACT75", label: "75% — family income ₹1–3 lakh" },
  { code: "DREAM50", label: "50% — family income ₹3–5 lakh" },
  { code: "FUTURE30", label: "30% — family income ₹5–10 lakh" },
];

const INCOME_BANDS = [
  "Below ₹1 lakh",
  "₹1–3 lakh",
  "₹3–5 lakh",
  "₹5–10 lakh",
  "Above ₹10 lakh",
];

const ScholarshipApply = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [award, setAward] = useState(params.get("code") ?? "FULLSCHOLAR100");
  const [income, setIncome] = useState("");
  const [school, setSchool] = useState("");
  const [girl, setGirl] = useState(false);
  const [firstGen, setFirstGen] = useState(false);
  const [ngo, setNgo] = useState(false);
  const [merit, setMerit] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<{ status: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("scholarship_applications")
        .select("status")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setExisting(data ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const submit = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!income) {
      toast.error("Please choose your family income range.");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("scholarship_applications").insert({
      user_id: user.id,
      status: "submitted",
      requested_benefit_id: award,
      institution_name: school.trim() || null,
      answers: {
        income_band: income,
        girl_student: girl,
        first_generation: firstGen,
        ngo_partner: ngo,
        merit,
        school: school.trim(),
        note: note.trim(),
      },
    });
    setSaving(false);
    if (error) {
      toast.error("We couldn't send that just now. Please try again.");
      return;
    }
    setExisting({ status: "submitted" });
    toast.success("Sent. We'll email you once someone has looked at it.");
  };

  if (authLoading) return null;

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8">
      <PageHead
        title="Apply for a scholarship — WeKIT Career Clarity 360"
        description="Tell us about your situation and we'll see which scholarship we can offer you."
        path="/scholarship"
      />

      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4">
        <Link to="/payment">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to checkout
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Apply for a scholarship</CardTitle>
          <CardDescription>
            Cost should not decide who gets clarity. Tell us where you are and
            we'll see what we can offer. A person reads every one of these.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {existing?.status === "approved" && (
            <p className="rounded-2xl bg-primary/10 p-4 text-sm">
              You're approved. Enter your scholarship code at checkout and it
              will unlock there.
            </p>
          )}
          {existing?.status === "submitted" && (
            <p className="rounded-2xl bg-muted p-4 text-sm">
              Your application is with us. We'll email you as soon as it has
              been looked at — you can send an updated one below if something
              has changed.
            </p>
          )}
          {existing?.status === "rejected" && (
            <p className="rounded-2xl bg-muted p-4 text-sm">
              We couldn't approve the last one. If your situation has changed,
              send it again below.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="award">Which scholarship are you asking about?</Label>
            <Select value={award} onValueChange={setAward}>
              <SelectTrigger id="award" className="min-h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AWARDS.map((a) => (
                  <SelectItem key={a.code} value={a.code}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="income">Family income each year</Label>
            <Select value={income} onValueChange={setIncome}>
              <SelectTrigger id="income" className="min-h-12">
                <SelectValue placeholder="Choose a range" />
              </SelectTrigger>
              <SelectContent>
                {INCOME_BANDS.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="school">School or college</Label>
            <Input
              id="school"
              className="min-h-12"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              placeholder="Where you study"
            />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">
              Any of these that apply to you
            </legend>
            {[
              { id: "girl", label: "I'm a girl student", value: girl, set: setGirl },
              {
                id: "firstgen",
                label: "I'm the first in my family to study this far",
                value: firstGen,
                set: setFirstGen,
              },
              {
                id: "ngo",
                label: "An NGO partner referred me",
                value: ngo,
                set: setNgo,
              },
              {
                id: "merit",
                label: "I have 85%+ marks, an award, or I'm a merit applicant",
                value: merit,
                set: setMerit,
              },
            ].map((c) => (
              <div key={c.id} className="flex items-start gap-3">
                <Checkbox
                  id={c.id}
                  checked={c.value}
                  onCheckedChange={(v) => c.set(v === true)}
                />
                <Label htmlFor={c.id} className="font-normal leading-6">
                  {c.label}
                </Label>
              </div>
            ))}
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="note">Anything else we should know?</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Optional"
            />
          </div>

          <Button
            className="min-h-12 w-full"
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Sending…" : "Send my application"}
          </Button>
          <p className="text-xs text-muted-foreground">
            We use this only to decide your scholarship.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScholarshipApply;
